(() => {
  class WemondExperienceProduct extends HTMLElement {
    connectedCallback() {
      const guideParams = new URLSearchParams(location.search);
      if (guideParams.get('guide') === '1') {
        const requested = String(guideParams.get('kahveler') || '').split(',').map((handle) => handle.trim()).filter(Boolean);
        if (requested.length === 3) {
          const root = window.Shopify?.routes?.root || '/';
          const url = new URL(`${root}pages/blend-lab`, location.origin);
          url.searchParams.set('tip', this.dataset.packageType === 'filter' ? 'filter' : 'espresso');
          url.searchParams.set('kahveler', requested.join(','));
          location.replace(url.toString());
          return;
        }
        this.renderGuideMode(guideParams);
        return;
      }

      this.panels = [...this.querySelectorAll('[data-step]')];
      this.selections = [null, null, null];
      this.activeStep = 0;
      this.mode = 'build'; // build | edit-overview | edit-flow | result
      this.transitioning = false;
      this.pendingTransition = null;
      this.variants = JSON.parse(this.querySelector('[data-parent-variants]')?.textContent || '[]');

      this.querySelectorAll('[data-option]').forEach((option) => {
        option.addEventListener('click', () => this.select(Number(option.dataset.stepIndex), option));
      });
      this.querySelectorAll('[data-step-head]').forEach((head, index) => {
        head.addEventListener('click', () => this.reopen(index));
      });
      this.querySelector('[data-add]')?.addEventListener('click', () => this.submit(false));
      this.querySelector('[data-buy]')?.addEventListener('click', () => this.submit(true));
      this.querySelector('[data-edit]')?.addEventListener('click', () => this.edit());
      this.querySelector('[data-return]')?.addEventListener('click', () => this.returnToResult());
      this.querySelector('[data-qty-minus]')?.addEventListener('click', () => this.setQuantity(this.quantity() - 1));
      this.querySelector('[data-qty-plus]')?.addEventListener('click', () => this.setQuantity(this.quantity() + 1));
      this.querySelector('[data-quantity-input]')?.addEventListener('change', (event) => this.setQuantity(event.currentTarget.value));
      this.querySelector('[data-quantity-input]')?.addEventListener('blur', (event) => this.setQuantity(event.currentTarget.value));

      this.render();
    }

    select(step, option) {
      if (step !== this.activeStep || this.transitioning || !['build', 'edit-flow'].includes(this.mode)) return;

      this.selections[step] = this.data(option);
      for (let index = step + 1; index < 3; index += 1) this.selections[index] = null;

      if (step === 2) {
        this.swapView(() => {
          this.activeStep = 3;
          this.mode = 'result';
        }, '[data-result]');
        return;
      }

      // Every product-selection change now uses the exact same leave/enter motion
      // as “Paketi düzenle” and “Ödeme ekranına geri dön”.
      this.swapView(() => {
        this.activeStep = step + 1;
      }, '[data-steps]');
    }

    reopen(step) {
      if (this.transitioning || this.mode !== 'edit-overview' || !this.selections[step]) return;

      // A change always invalidates that slot and every slot after it.
      // 1 -> reselect 1,2,3 | 2 -> reselect 2,3 | 3 -> reselect 3.
      this.swapView(() => {
        for (let index = step; index < 3; index += 1) this.selections[index] = null;
        this.activeStep = step;
        this.mode = 'edit-flow';
      }, '[data-steps]');
    }

    edit() {
      if (this.transitioning || !this.selections.every(Boolean)) return;
      this.swapView(() => {
        this.mode = 'edit-overview';
        this.activeStep = 3;
      }, '[data-steps]');
    }

    returnToResult() {
      if (!this.selections.every(Boolean) || this.transitioning || this.mode !== 'edit-overview') return;
      this.swapView(() => {
        this.mode = 'result';
        this.activeStep = 3;
      }, '[data-result]');
    }

    async swapView(updateState, targetSelector) {
      if (this.transitioning) {
        this.pendingTransition = { updateState, targetSelector };
        return;
      }
      const outgoing = this.querySelector('[data-result]:not([hidden]), [data-steps]:not([hidden])');
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.transitioning = true;

      try {
        if (outgoing && !reduced) {
          outgoing.getAnimations().forEach(animation => animation.cancel());
          const leave = outgoing.animate(
            [{ opacity: 1, transform: 'translate3d(0,0,0)' }, { opacity: 0, transform: 'translate3d(-18px,0,0)' }],
            { duration: 250, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'forwards' }
          );
          await leave.finished.catch(() => {});
          /* Freeze the exact last frame before cancelling WAAPI. This prevents
             the one-frame return to opacity:1 that caused the visible blink. */
          outgoing.style.opacity = '0';
          leave.cancel();
        }

        updateState();
        this.render();

        const incoming = this.querySelector(`${targetSelector}:not([hidden])`);
        if (incoming) {
          incoming.getAnimations().forEach(animation => animation.cancel());
          if (!reduced) {
            incoming.style.opacity = '0';
            const enter = incoming.animate(
              [{ opacity: 0, transform: 'translate3d(22px,0,0)' }, { opacity: 1, transform: 'translate3d(0,0,0)' }],
              { duration: 420, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'forwards' }
            );
            await enter.finished.catch(() => {});
            incoming.style.opacity = '1';
            enter.cancel();
          }
          incoming.style.opacity = '';
          incoming.style.transform = '';
        }
        if (outgoing && outgoing !== incoming) { outgoing.style.opacity = ''; outgoing.style.transform = ''; }
      } finally {
        this.transitioning = false;
        const queued = this.pendingTransition;
        this.pendingTransition = null;
        if (queued) this.swapView(queued.updateState, queued.targetSelector);
      }
    }

    data(option) {
      return {
        code: option.dataset.code,
        handle: option.dataset.handle,
        title: option.dataset.title,
        price: Number(option.dataset.price),
        profile: option.dataset.profile,
        process: option.dataset.process,
        body: Number(option.dataset.body),
        acidity: Number(option.dataset.acidity),
        priority: Number(option.dataset.priority),
        note: option.dataset.note || option.querySelector('.wxp-option__copy small')?.textContent?.trim() || '',
        image: option.dataset.image || option.querySelector('.wxp-option__image img')?.currentSrc || option.querySelector('.wxp-option__image img')?.src || ''
      };
    }

    score(option, step) {
      let score = Number(option.dataset.priority || 0);
      const chosen = this.selections.slice(0, step).filter(Boolean);
      if (!chosen.length) return score;

      const profiles = new Set(chosen.map((item) => item.profile));
      const processes = new Set(chosen.map((item) => item.process));
      const body = chosen.reduce((sum, item) => sum + item.body, 0) / chosen.length;
      const acidity = chosen.reduce((sum, item) => sum + item.acidity, 0) / chosen.length;
      if (!profiles.has(option.dataset.profile)) score += 26;
      if (!processes.has(option.dataset.process)) score += 7;
      score += Math.abs(Number(option.dataset.body) - body) * 5;
      score += Math.abs(Number(option.dataset.acidity) - acidity) * 5;
      return score;
    }

    normalizeVariantText(value = '') {
      return String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/ı/g, 'i')
        .toLowerCase();
    }

    variantCode(variant) {
      const packageCode = this.dataset.packageType === 'filter' ? 'FIL' : 'ESP';
      const skuMatch = String(variant?.sku || '').trim().toUpperCase().match(new RegExp(`^EXP-${packageCode}-([A-Z]{3})$`));
      if (skuMatch) return [...skuMatch[1]].sort().join('+');

      const aliases = this.dataset.packageType === 'filter'
        ? {
            C: ['huila', 'colombia'],
            E: ['yirgacheffe', 'ethiopia'],
            G: ['antigua', 'guatemala'],
            H: ['honduras']
          }
        : {
            B: ['cerrado', 'brazil'],
            C: ['huila', 'colombia'],
            G: ['antigua', 'guatemala'],
            H: ['honduras']
          };
      const title = this.normalizeVariantText(variant?.title || '');
      const codes = Object.entries(aliases)
        .filter(([, names]) => names.some((name) => title.includes(name)))
        .map(([code]) => code)
        .sort();
      return codes.length === 3 ? codes.join('+') : '';
    }

    variant(normalTotal) {
      if (this.selections.some((item) => !item)) return null;
      const key = this.selections.map((item) => item.code).sort().join('+');
      const exact = this.variants.find(
        (variant) => variant.available !== false && this.variantCode(variant) === key
      );
      if (exact) return exact;

      // A price is only a safe fallback when it identifies exactly one
      // available variant. Several package combinations intentionally share
      // the same price, so choosing the first match would attach the wrong
      // Shopify variant to the customer's coffee selection.
      const expectedPrice = normalTotal - 15000;
      const priceMatches = this.variants.filter(
        (variant) => variant.available !== false && Number(variant.price) === expectedPrice
      );
      return priceMatches.length === 1 ? priceMatches[0] : null;
    }

    money(cents) {
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(cents / 100);
    }

    escape(value = '') {
      const div = document.createElement('div');
      div.textContent = value;
      return div.innerHTML;
    }

    cleanTitle(value = '') {
      return String(value)
        .replace(/\s*[–-]\s*250\s*g(?:r)?\b/gi, '')
        .replace(/\s+/g, ' ')
        .trim();
    }

    // After a successful package add, behave like a normal product add: close the experience
    // launcher first (when the builder runs inside it), then open the existing Wemond mini cart,
    // so the two overlays never stack. On the product page the mini cart opens directly.
    showMiniCartAfterAdd() {
      const miniCart = window.WemondMiniCart;
      if (!miniCart || typeof miniCart.open !== 'function') return;
      const launcher = this.closest('wemond-experience-launcher');
      if (launcher && launcher.classList.contains('is-open') && typeof launcher.close === 'function') {
        launcher.close();
        window.setTimeout(() => miniCart.open({ announceAdd: true }), 480);
      } else {
        miniCart.open({ announceAdd: true });
      }
    }

    guideUrl() {
      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(`${root}pages/blend-lab`, location.origin);
      url.searchParams.set('tip', this.dataset.packageType === 'filter' ? 'filter' : 'espresso');
      url.searchParams.set('kahveler', this.selections.map((item) => item.handle).join(','));
      return url.toString();
    }

    experienceRecipe(handle, methodKey) {
      return window.WemondRecipeUtils?.find?.(handle, methodKey) || null;
    }

    labCoffee(handle) {
      const catalog = {
        'brazil-cerrado': {
          short: 'Brazil Cerrado',
          notes: ['Sütlü çikolata', 'Karamel', 'Fındık'],
          increase: 'Brazil’i artırdıkça fincan daha tatlı, daha yuvarlak ve daha çikolatalı olur. Asidite biraz sakinleşir; gövde güçlenir.',
          espresso: { sweetness: 4.8, body: 4.5, brightness: 1.3, depth: 3.6, clarity: 2.8 },
          filter: { sweetness: 4.4, body: 4.0, brightness: 1.5, depth: 3.2, clarity: 3.0 }
        },
        'colombia-huila-supremo': {
          short: 'Colombia Huila',
          notes: ['Kırmızı Meyve', 'Karamel', 'Narenciye', 'Sütlü Çikolata'],
          increase: 'Huila’yı artırdıkça fincan daha temiz ve canlı olur. Karamel korunur; kırmızı meyve ve narenciye biraz daha belirginleşir.',
          espresso: { sweetness: 4.0, body: 3.3, brightness: 3.4, depth: 3.0, clarity: 4.2 },
          filter: { sweetness: 4.0, body: 3.0, brightness: 3.6, depth: 2.8, clarity: 4.4 }
        },
        'guatemala-antigua-shb-250g': {
          short: 'Guatemala Antigua',
          notes: ['Bitter Çikolata', 'Karamel', 'Tatlı Baharat', 'Narenciye'],
          increase: 'Antigua’yı artırdıkça derinlik ve bitiş güçlenir; bitter çikolata, karamel ve tatlı baharat daha belirginleşir, narenciye fincanı canlı tutar.',
          espresso: { sweetness: 4.1, body: 4.0, brightness: 2.5, depth: 4.6, clarity: 3.4 },
          filter: { sweetness: 4.0, body: 3.7, brightness: 2.8, depth: 4.3, clarity: 3.6 }
        },
        'honduras-shg': {
          short: 'Honduras SHG',
          notes: ['Kakao', 'Fındık', 'Karamel'],
          increase: 'Honduras’ı artırdıkça fincan daha yumuşak, fındıksı ve tok olur. Canlı meyve notaları geri çekilir, günlük içimi kolaylaşır.',
          espresso: { sweetness: 4.2, body: 4.3, brightness: 1.9, depth: 3.8, clarity: 3.0 },
          filter: { sweetness: 4.1, body: 4.0, brightness: 2.1, depth: 3.7, clarity: 3.2 }
        },
        'ethiopia-yirgacheffe': {
          short: 'Ethiopia Yirgacheffe',
          notes: ['Yasemin', 'Bergamot', 'Beyaz Şeftali', 'Bal', 'Siyah Çay', 'Limon Kabuğu'],
          increase: 'Yirgacheffe’yi artırdıkça fincan daha çiçeksi, narenciyeli ve hafif olur. Gövde incelir; koku ve canlılık belirginleşir.',
          espresso: { sweetness: 3.7, body: 2.1, brightness: 4.8, depth: 2.2, clarity: 4.8 },
          filter: { sweetness: 3.8, body: 2.0, brightness: 4.9, depth: 2.1, clarity: 4.9 }
        }
      };
      const fallback = {
        short: this.cleanTitle(handle),
        notes: ['Tatlılık', 'Denge', 'Temiz fincan'],
        increase: 'Bu kahveyi artırdıkça kendi karakteri blend içinde daha belirgin hale gelir.',
        espresso: { sweetness: 3, body: 3, brightness: 3, depth: 3, clarity: 3 },
        filter: { sweetness: 3, body: 3, brightness: 3, depth: 3, clarity: 3 }
      };
      return catalog[handle] || fallback;
    }

    blendVector(chosen, ratios) {
      const mode = this.dataset.packageType === 'espresso' ? 'espresso' : 'filter';
      const keys = ['sweetness', 'body', 'brightness', 'depth', 'clarity'];
      return keys.reduce((result, key) => {
        result[key] = chosen.reduce((sum, item, index) => {
          const profile = this.labCoffee(item.handle)[mode];
          return sum + Number(profile[key] || 3) * (Number(ratios[index] || 0) / 100);
        }, 0);
        return result;
      }, {});
    }

    blendNotes(chosen, ratios) {
      const scores = new Map();
      chosen.forEach((item, index) => {
        const coffee = this.labCoffee(item.handle);
        const share = Number(ratios[index] || 0) / 100;
        coffee.notes.forEach((note, noteIndex) => {
          const weight = [1, .78, .58][noteIndex] || .5;
          scores.set(note, (scores.get(note) || 0) + share * weight);
        });
      });
      return [...scores.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3).map(([note]) => note);
    }

    blendSummary(chosen, ratios) {
      const vector = this.blendVector(chosen, ratios);
      const notes = this.blendNotes(chosen, ratios);
      const body = vector.body >= 4.05 ? 'dolgun' : vector.body >= 3.25 ? 'orta ve yuvarlak' : 'hafif';
      const brightness = vector.brightness >= 4.05 ? 'canlı' : vector.brightness >= 3.15 ? 'dengeli canlılıkta' : 'yumuşak asiditeli';
      const finish = vector.depth >= 4.0 ? 'uzun ve derin' : vector.clarity >= 4.0 ? 'temiz ve net' : 'tatlı ve dengeli';
      return `${notes.join(' • ')}. ${body.charAt(0).toUpperCase() + body.slice(1)} gövde, ${brightness}; bitiş ${finish}.`;
    }

    presetBlends(chosen) {
      const mode = this.dataset.packageType === 'espresso' ? 'espresso' : 'filter';
      const presets = mode === 'espresso'
        ? [
            { name: 'Yumuşak & Tatlı', copy: 'Çikolata ve karamel önde; kolay içimli başlangıç.', weights: [55, 30, 15], target: { sweetness: 1.5, body: 1.05, brightness: -.75, depth: .25, clarity: .1 } },
            { name: 'Dengeli', copy: 'Üç kahveyi de duyabileceğin orta yol.', weights: [40, 35, 25], target: { sweetness: .8, body: .55, brightness: .45, depth: .55, clarity: .8 } },
            { name: 'Daha Karakterli', copy: 'Meyve, baharat veya derinlik daha belirgin.', weights: [50, 30, 20], target: { sweetness: .2, body: .15, brightness: .85, depth: 1.15, clarity: .45 } }
          ]
        : [
            { name: 'Temiz & Canlı', copy: 'Koku ve canlılık önde; daha hafif fincan.', weights: [50, 30, 20], target: { sweetness: .2, body: -.45, brightness: 1.45, depth: .05, clarity: 1.25 } },
            { name: 'Dengeli', copy: 'Tatlılık, gövde ve canlılık aynı fincanda.', weights: [40, 35, 25], target: { sweetness: .75, body: .45, brightness: .55, depth: .45, clarity: .85 } },
            { name: 'Tatlı & Dolgun', copy: 'Karamel ve kakao tarafı daha belirgin.', weights: [50, 30, 20], target: { sweetness: 1.25, body: 1.1, brightness: -.3, depth: .75, clarity: .2 } }
          ];

      return presets.map((preset) => {
        const ranked = chosen.map((item, index) => {
          const vector = this.labCoffee(item.handle)[mode];
          const score = Object.entries(preset.target).reduce((sum, [key, weight]) => sum + Number(vector[key] || 3) * weight, 0);
          return { index, score };
        }).sort((a, b) => b.score - a.score);
        const ratios = [0, 0, 0];
        ranked.forEach((entry, rank) => { ratios[entry.index] = preset.weights[rank]; });
        return { ...preset, ratios };
      });
    }

    blendRecipe(chosen, ratios) {
      const espresso = this.dataset.packageType === 'espresso';
      const vector = this.blendVector(chosen, ratios);
      const grams = ratios.map((value) => (value * (espresso ? .18 : .15)).toFixed(1));
      const mixLine = chosen.map((item, index) => `${grams[index]} g ${this.labCoffee(item.handle).short}`).join(' + ');
      const summary = this.blendSummary(chosen, ratios);

      if (espresso) {
        const output = Math.max(38, Math.min(42, Math.round(40 + (vector.brightness - 3) * .7 - (vector.body - 3) * .45)));
        const temperature = vector.brightness > 3.7 ? '93°C' : vector.body > 4.0 ? '92°C' : '92–93°C';
        const time = vector.body > 4.05 ? '28–31 sn' : '27–30 sn';
        return {
          lead: summary,
          metrics: [
            ['Kahve', '18 g'], ['Çıktı', `${output} g`], ['Sıcaklık', temperature], ['Öğütüm', 'İnce'], ['Süre', time], ['Karışım', ratios.map((value) => `%${value}`).join(' / ')]
          ],
          steps: [
            `Kahveleri tart: ${mixLine}.`,
            'Çekirdekleri küçük bir kapta karıştır ve birlikte öğüt.',
            '18 g kahveyi portafiltreye aktar; eşit dağıt ve düz tamp yap.',
            `${output} g çıktıyı ${time.replace(' sn', ' saniye')} civarında hedefle.`,
            'Shotı karıştır, bir yudum al ve önce tadı değerlendir; sonra yalnızca tek bir şeyi değiştir.'
          ],
          fine: 'Ekşi ve ince geliyorsa öğütümü 1 küçük adım incelt. Acı, kuru veya fazla ağırsa 1 küçük adım kalınlaştır. Demleme doğru ama tadın yönünü değiştirmek istiyorsan öğütüme değil yüzdelere dokun.'
        };
      }

      const temperature = vector.brightness > 4.0 ? '93–94°C' : vector.body > 4.0 ? '92–93°C' : '93°C';
      const duration = vector.body > 4.0 ? '2:55–3:15' : '2:45–3:05';
      return {
        lead: summary,
        metrics: [
          ['Kahve', '15 g'], ['Su', '250 g'], ['Sıcaklık', temperature], ['Öğütüm', 'Orta-ince'], ['Süre', duration], ['Karışım', ratios.map((value) => `%${value}`).join(' / ')]
        ],
        steps: [
          `Kahveleri tart: ${mixLine}.`,
          'Çekirdekleri karıştır ve birlikte orta-ince öğüt.',
          '40 g suyla bütün yatağı ıslat; 40 saniye bekle.',
          '140 g seviyesine sakin bir döküş yap; ardından 250 g’a tamamla.',
          `Demlemenin ${duration} aralığında bitmesini hedefle ve serverı hafifçe çevirip servis et.`
        ],
        fine: 'Fincan keskin veya zayıfsa bir küçük adım incelt. Kuru, ağır veya yavaşsa bir küçük adım kalınlaştır. Demleme temiz ama karakteri değiştirmek istiyorsan yüzdelerle oyna.'
      };
    }

    renderMetrics(metrics) {
      return metrics.map(([label, value]) => `<span><small>${this.escape(label)}</small><b>${this.escape(value)}</b></span>`).join('');
    }

    singleGuideMarkup(item, index, methodKey) {
      const recipe = this.experienceRecipe(item.handle, methodKey);
      const profile = window.WemondProductRecipes?.[item.handle]?.profile || item.note || '';
      if (!recipe) return `<div class="wxp-guide-error"><strong>Reçete hazırlanıyor.</strong><span>${this.escape(this.cleanTitle(item.title))} için başlangıç reçetesi yakında burada olacak.</span></div>`;
      const grindLabel = window.WemondRecipeUtils?.grinderLabel?.(methodKey) || (methodKey === 'espresso' ? 'İnce' : 'Orta-ince');
      const grinderRefs = window.WemondRecipeUtils?.grinderRefs?.(recipe.grind, methodKey) || recipe.grind || '';
      return `
        <article class="wxp-lab-recipe">
          <div class="wxp-lab-recipe__head">
            <div><small>${index + 1}. kahve · ${methodKey === 'espresso' ? 'Espresso' : 'V60'}</small><h2>${this.escape(this.cleanTitle(item.title))}</h2></div>
            <p>${this.escape(profile)}</p>
          </div>
          <p class="wxp-lab-recipe__lead">${this.escape(recipe.note || 'Bu reçeteyi başlangıç noktası olarak kullan; tadı küçük öğütüm değişiklikleriyle yerine oturt.')}</p>
          <div class="wxp-lab-recipe__metrics">
            ${this.renderMetrics([['Oran', recipe.ratio], ['Sıcaklık', recipe.temperature], ['Öğütüm', grindLabel], ['Süre', recipe.duration]])}
          </div>
          <div class="wxp-lab-recipe__grinder"><small>Değirmen referansı</small><strong>${this.escape(grinderRefs)}</strong></div>
          <div class="wxp-lab-recipe__steps"><small>Adım adım</small><ol>${recipe.steps_html || ''}</ol></div>
          <div class="wxp-lab-recipe__fine"><small>Fincanı ince ayarla</small><p>Ekşi ve zayıfsa biraz incelt; acı, kuru veya çok yavaşsa biraz kalınlaştır. İlk denemede yalnızca öğütümü değiştir.</p></div>
        </article>`;
    }

    presetMarkup(chosen, preset, index) {
      const recipe = this.blendRecipe(chosen, preset.ratios);
      const ratioText = chosen.map((item, itemIndex) => `${this.labCoffee(item.handle).short} %${preset.ratios[itemIndex]}`).join(' · ');
      return `
        <article class="wxp-blend-preset">
          <button type="button" class="wxp-blend-preset__head" data-blend-preset="${index}">
            <span><small>Başlangıç blendi</small><strong>${this.escape(preset.name)}</strong></span>
            <em>Bu oranı dene</em>
          </button>
          <p>${this.escape(preset.copy)}</p>
          <div class="wxp-blend-preset__ratio">${this.escape(ratioText)}</div>
          <p class="wxp-blend-preset__taste"><strong>Beklenen tat:</strong> ${this.escape(recipe.lead)}</p>
          <div class="wxp-blend-preset__steps"><small>Nasıl demle?</small><ol>${recipe.steps.map((step) => `<li>${this.escape(step)}</li>`).join('')}</ol></div>
        </article>`;
    }

    normalizeBlend(index, delta) {
      if (!this.guideRatios) return;
      const next = [...this.guideRatios];
      const min = 10;
      const max = 80;
      if (delta > 0) {
        if (next[index] >= max) return;
        const donors = [0, 1, 2].filter((i) => i !== index && next[i] > min).sort((a, b) => next[b] - next[a]);
        if (!donors.length) return;
        const donor = donors[0];
        const amount = Math.min(5, next[donor] - min, max - next[index]);
        next[index] += amount;
        next[donor] -= amount;
      } else {
        if (next[index] <= min) return;
        const receivers = [0, 1, 2].filter((i) => i !== index && next[i] < max).sort((a, b) => next[a] - next[b]);
        if (!receivers.length) return;
        const receiver = receivers[0];
        const amount = Math.min(5, next[index] - min, max - next[receiver]);
        next[index] -= amount;
        next[receiver] += amount;
      }
      this.guideRatios = next;
      this.renderCustomBlend();
    }

    renderCustomBlend() {
      const chosen = this.guideChosen;
      const ratios = this.guideRatios;
      if (!chosen || !ratios) return;
      const espresso = this.dataset.packageType === 'espresso';
      const dose = espresso ? 18 : 15;
      this.querySelectorAll('[data-blend-row]').forEach((row, index) => {
        row.querySelector('[data-blend-percent]').textContent = `%${ratios[index]}`;
        row.querySelector('[data-blend-grams]').textContent = `${(dose * ratios[index] / 100).toFixed(1)} g`;
        row.querySelector('[data-blend-minus]').disabled = ratios[index] <= 10;
        row.querySelector('[data-blend-plus]').disabled = ratios[index] >= 80;
      });
      const recipe = this.blendRecipe(chosen, ratios);
      const summary = this.querySelector('[data-custom-summary]');
      const metrics = this.querySelector('[data-custom-metrics]');
      const steps = this.querySelector('[data-custom-steps]');
      const fine = this.querySelector('[data-custom-fine]');
      if (summary) summary.textContent = recipe.lead;
      if (metrics) metrics.innerHTML = this.renderMetrics(recipe.metrics);
      if (steps) steps.innerHTML = recipe.steps.map((step) => `<li>${this.escape(step)}</li>`).join('');
      if (fine) fine.textContent = recipe.fine;
    }

    activateGuideTab(name) {
      this.querySelectorAll('[data-guide-tab]').forEach((button) => {
        const active = button.dataset.guideTab === name;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this.querySelectorAll('[data-guide-panel]').forEach((panel) => { panel.hidden = panel.dataset.guidePanel !== name; });
    }

    renderGuideMode(params) {
      const builder = this.querySelector('.wxp__builder');
      const guide = this.querySelector('[data-guide]');
      const intro = this.querySelector('[data-guide-intro]');
      if (!guide) return;

      if (builder) builder.hidden = true;
      guide.hidden = false;
      this.setAttribute('data-guide-mode', 'true');

      const requested = String(params.get('kahveler') || '').split(',').map((handle) => handle.trim()).filter(Boolean);
      const handles = [...new Set(requested)];
      const catalog = new Map();
      this.querySelectorAll('[data-option][data-handle]').forEach((option) => {
        if (!catalog.has(option.dataset.handle)) catalog.set(option.dataset.handle, this.data(option));
      });
      const chosen = handles.map((handle) => catalog.get(handle)).filter(Boolean);

      if (handles.length !== 3 || chosen.length !== 3) {
        if (intro) intro.textContent = 'Bu rehber bağlantısındaki kahve seçimi eksik veya geçersiz görünüyor.';
        const panel = this.querySelector('[data-guide-panel="single"]');
        if (panel) panel.innerHTML = '<div class="wxp-guide-error"><strong>Rehber oluşturulamadı.</strong><span>Sipariş e-postandaki kişisel bağlantıyı yeniden açabilir veya paket sayfasından yeni bir üçlü oluşturabilirsin.</span></div>';
        return;
      }

      this.guideChosen = chosen;
      const methodKey = this.dataset.packageType === 'espresso' ? 'espresso' : 'v60';
      if (intro) intro.textContent = 'Önce kahveleri tek tek tanı. Sonra oranlarla oyna, fincanın nasıl değiştiğini gör. Buradaki her reçete bir başlangıç noktası; en iyi blend, senin sevdiğin blend.';

      const singleTabs = this.querySelector('[data-single-tabs]');
      const singleRecipe = this.querySelector('[data-single-recipe]');
      if (singleTabs && singleRecipe) {
        singleTabs.innerHTML = chosen.map((item, index) => `<button type="button" data-single-coffee="${index}" class="${index === 0 ? 'is-active' : ''}">${this.escape(this.labCoffee(item.handle).short)}</button>`).join('');
        const showSingle = (index) => {
          singleTabs.querySelectorAll('[data-single-coffee]').forEach((button) => button.classList.toggle('is-active', Number(button.dataset.singleCoffee) === index));
          singleRecipe.innerHTML = this.singleGuideMarkup(chosen[index], index, methodKey);
        };
        singleTabs.querySelectorAll('[data-single-coffee]').forEach((button) => button.addEventListener('click', () => showSingle(Number(button.dataset.singleCoffee))));
        showSingle(0);
      }

      const presets = this.presetBlends(chosen);
      this.guidePresets = presets;
      const presetList = this.querySelector('[data-blend-presets]');
      if (presetList) presetList.innerHTML = presets.map((preset, index) => this.presetMarkup(chosen, preset, index)).join('');

      this.guideRatios = [...presets[1].ratios];
      const controls = this.querySelector('[data-blend-controls]');
      if (controls) {
        controls.innerHTML = chosen.map((item, index) => `
          <div class="wxp-blend-control" data-blend-row="${index}">
            <div><strong>${this.escape(this.labCoffee(item.handle).short)}</strong><small>${this.escape(this.labCoffee(item.handle).notes.slice(0, 2).join(' · '))}</small></div>
            <div class="wxp-blend-control__buttons">
              <button type="button" data-blend-minus aria-label="${this.escape(this.labCoffee(item.handle).short)} oranını azalt">−</button>
              <b data-blend-percent>%0</b>
              <button type="button" data-blend-plus aria-label="${this.escape(this.labCoffee(item.handle).short)} oranını artır">+</button>
            </div>
            <span data-blend-grams>0 g</span>
          </div>`).join('');
        controls.querySelectorAll('[data-blend-row]').forEach((row, index) => {
          row.querySelector('[data-blend-minus]').addEventListener('click', () => this.normalizeBlend(index, -5));
          row.querySelector('[data-blend-plus]').addEventListener('click', () => this.normalizeBlend(index, 5));
        });
      }

      presetList?.querySelectorAll('[data-blend-preset]').forEach((button) => button.addEventListener('click', () => {
        const preset = presets[Number(button.dataset.blendPreset)];
        if (!preset) return;
        this.guideRatios = [...preset.ratios];
        this.renderCustomBlend();
        this.querySelector('[data-custom-blend]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }));
      this.renderCustomBlend();

      const steerList = this.querySelector('[data-steer-list]');
      if (steerList) {
        steerList.innerHTML = chosen.map((item) => {
          const coffee = this.labCoffee(item.handle);
          return `<article class="wxp-steer-card"><small>Bu kahveyi +%10 artırırsan</small><h3>${this.escape(coffee.short)}</h3><p>${this.escape(coffee.increase)}</p><span>${this.escape(coffee.notes.join(' • '))}</span></article>`;
        }).join('');
      }

      this.querySelectorAll('[data-guide-tab]').forEach((button) => button.addEventListener('click', () => this.activateGuideTab(button.dataset.guideTab)));
      this.activateGuideTab('single');
    }


    quantity() {
      const input = this.querySelector('[data-quantity-input]');
      return Math.max(1, Number(input?.value) || 1);
    }

    setQuantity(value) {
      const input = this.querySelector('[data-quantity-input]');
      if (!input) return;
      const min = Math.max(1, Number(input.min) || 1);
      const max = input.max ? Number(input.max) : Infinity;
      input.value = String(Math.min(max, Math.max(min, Number(value) || min)));
    }

    async refreshCartUI() {
      const root = window.Shopify?.routes?.root || '/';
      const sectionUrl = new URL(root, location.origin);
      sectionUrl.searchParams.set('section_id', 'cart-drawer-section');

      const [cartResponse, drawerResponse] = await Promise.all([
        fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' }),
        fetch(sectionUrl.toString(), { headers: { Accept: 'text/html' }, cache: 'no-store' })
      ]);

      const cart = cartResponse.ok ? await cartResponse.json() : null;

      // Keep the drawer data fresh without opening it. The user stays on the package page.
      if (drawerResponse.ok) {
        const doc = new DOMParser().parseFromString(await drawerResponse.text(), 'text/html');
        const freshDrawer = doc.querySelector('theme-drawer#cart-drawer');
        const currentDrawer = document.querySelector('theme-drawer#cart-drawer');
        const freshInner = freshDrawer?.querySelector('[data-hydration-key="cart-drawer-inner"]');
        const currentInner = currentDrawer?.querySelector('[data-hydration-key="cart-drawer-inner"]');
        if (freshInner && currentInner) currentInner.replaceWith(freshInner);
        const freshEmpty = freshDrawer?.querySelector('#empty-cart-template');
        const currentEmpty = currentDrawer?.querySelector('#empty-cart-template');
        if (freshEmpty && currentEmpty) currentEmpty.innerHTML = freshEmpty.innerHTML;
      }

      const cartIcon = document.querySelector('cart-icon');
      if (cart && cartIcon && typeof cartIcon.renderCartBubble === 'function') {
        await cartIcon.renderCartBubble(cart.item_count || 0);
      }
    }

    render() {
      const steps = this.querySelector('[data-steps]');
      const result = this.querySelector('[data-result]');
      const ready = this.selections.every(Boolean);
      const showResult = this.mode === 'result' && ready;
      const editOverview = this.mode === 'edit-overview' && ready;
      const focusedEdit = this.mode === 'edit-flow';
      const usedCodes = this.selections.filter(Boolean).map((item) => item.code);

      steps.hidden = showResult;
      result.hidden = !showResult;
      steps.classList.toggle('is-edit-overview', editOverview);
      this.querySelector('[data-return]').hidden = !editOverview;
      this.querySelector('[data-return-row]').hidden = !editOverview;

      this.panels.forEach((panel, step) => {
        const complete = Boolean(this.selections[step]);
        const active = step === this.activeStep && this.activeStep < 3 && !editOverview;

        let visible = false;
        if (editOverview) {
          visible = true;
        } else if (focusedEdit) {
          // Earlier retained choices stay as summaries, but only the current slot exposes product options.
          visible = step <= this.activeStep && (complete || active);
        } else if (this.mode === 'build') {
          // Initial flow: completed choices remain exactly where their selection heading was.
          visible = step <= this.activeStep;
        }

        panel.hidden = !visible;
        panel.classList.toggle('is-complete', complete);
        panel.classList.toggle('is-active', active);
        panel.classList.toggle('is-locked', visible && !active && !complete);
        panel.classList.toggle('is-edit-overview', editOverview);

        const head = panel.querySelector('[data-step-head]');
        head.setAttribute('aria-expanded', String(active));
        // Completed rows become editable only after “Paketi düzenle” is opened.
        head.disabled = !(editOverview && complete);

        const heading = panel.querySelector('.wxp-step__heading strong');
        if (heading) heading.textContent = complete ? `${step + 1}. Kahve` : `${step + 1}. kahveni seç`;

        panel.querySelector('[data-step-state]').textContent = editOverview && complete ? 'Değiştir' : '';
        panel.querySelector('[data-step-summary]').textContent = complete
          ? this.selections[step].title
          : step === 0
            ? 'Tüm uygun kahveler'
            : 'Önceki seçime göre sıralanacak';

        const thumb = panel.querySelector('[data-step-thumb]');
        const description = panel.querySelector('[data-step-description]');
        const showEditorialSummary = editOverview && complete;
        if (thumb) {
          thumb.hidden = !showEditorialSummary;
          thumb.innerHTML = showEditorialSummary && this.selections[step].image
            ? `<img src="${this.escape(this.selections[step].image)}" alt="" loading="lazy">`
            : '';
        }
        if (description) {
          description.hidden = !showEditorialSummary;
          description.textContent = showEditorialSummary
            ? [this.selections[step].note, this.selections[step].process].filter(Boolean).join(' · ')
            : '';
        }

        const options = [...panel.querySelectorAll('[data-option]')];
        const available = options
          .filter((option) => !usedCodes.includes(option.dataset.code))
          .sort((a, b) => this.score(b, step) - this.score(a, step));

        options.forEach((option) => {
          option.hidden = true;
          option.classList.remove('is-recommended');
        });
        if (active) {
          available.forEach((option, index) => {
            option.hidden = false;
            option.style.order = index;
            option.classList.toggle('is-recommended', index === 0 && step > 0);
          });
        }
      });

      if (!ready) return;

      const normalTotal = this.selections.reduce((sum, item) => sum + item.price, 0);
      const variant = this.variant(normalTotal);
      this.querySelector('[data-selection-list]').innerHTML = this.selections
        .map((item, index) => `
          <li class="wxp-selected-card">
            <span class="wxp-selected-card__image" aria-hidden="true">${item.image ? `<img src="${this.escape(item.image)}" alt="" loading="lazy">` : ''}</span>
            <span class="wxp-selected-card__copy">
              <span class="wxp-selected-card__index">${index + 1}. Kahve</span>
              <strong>${this.escape(item.title)}</strong>
              <small>${this.escape(item.note)}</small>
              <em>${this.escape(item.process)}</em>
            </span>
          </li>
        `)
        .join('');
      this.querySelector('[data-regular-total]').textContent = this.money(normalTotal);
      this.querySelector('[data-package-total]').textContent = variant
        ? this.money(variant.price)
        : this.money(normalTotal - 15000);
      this.querySelectorAll('[data-add],[data-buy]').forEach((button) => {
        button.disabled = !variant;
      });
      this.querySelector('[data-status]').textContent = variant
        ? ''
        : 'Bu üçlü için ürün varyantı Shopify yönetiminde henüz oluşturulmamış.';
    }

    async submit(buyNow) {
      const normalTotal = this.selections.reduce((sum, item) => sum + item.price, 0);
      const variant = this.variant(normalTotal);
      if (!variant) return;

      const quantity = this.quantity();
      const buttons = this.querySelectorAll('[data-add],[data-buy]');
      const addButton = this.querySelector('[data-add]');
      const addLabel = this.querySelector('[data-add-label]');
      const status = this.querySelector('[data-status]');

      buttons.forEach((button) => { button.disabled = true; });
      if (!buyNow) addLabel.textContent = 'Ekleniyor…';
      status.textContent = 'Paket hazırlanıyor…';

      const blendLabUrl = this.guideUrl();
      const blendLabHandles = this.selections.map((item) => item.handle).join(',');
      const properties = {
        'Paket': this.dataset.packageName,
        'Toplam': '3 × 250 g',
        'Blend Lab rehberi': 'Kişisel bağlantı siparişten sonra e-posta ile gönderilecek',
        '_wemond_blend_lab_url': blendLabUrl,
        '_wemond_blend_lab_type': this.dataset.packageType === 'filter' ? 'filter' : 'espresso',
        '_wemond_blend_lab_coffees': blendLabHandles,
        '_wemond_recipe_code': `${this.dataset.packageType}:${this.selections.map((item) => item.code).sort().join('+')}`,
        'Fidan bağışı': 'Siparişi veren kişi adına yapılacak'
      };
      this.selections.forEach((item, index) => {
        properties[`${index + 1}. Kahve`] = item.title;
      });

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variant.id), quantity, properties }] })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Paket sepete eklenemedi.');

        if (buyNow) {
          // Checkout requires a saved il/ilçe pair: the gate either continues
          // to checkout or opens the mini cart with the selector.
          if (window.WemondTrLocation?.goToCheckout) {
            const continued = await window.WemondTrLocation.goToCheckout();
            if (!continued) {
              buttons.forEach((button) => { button.disabled = false; });
              status.textContent = 'Paket sepete eklendi. Devam etmek için il ve ilçenizi seçin.';
            }
            return;
          }
          location.href = `${root}checkout`;
          return;
        }

        // Use the exact native theme success state instead of a separate package-only animation.
        // Start it immediately after Shopify confirms the add, just like a normal product button.
        addLabel.textContent = 'Sepete ekle';
        addButton.dataset.added = 'true';
        status.textContent = `${quantity} adet paket sepete eklendi.`;

        setTimeout(() => {
          addButton.removeAttribute('data-added');
        }, 800);

        // Refresh cart count/drawer contents silently. A refresh failure must never turn a
        // successful add into a visible error or encourage an accidental duplicate add.
        try { await this.refreshCartUI(); } catch (_) {}
        buttons.forEach((button) => { button.disabled = false; });
        this.showMiniCartAfterAdd();
      } catch (error) {
        addButton.removeAttribute('data-added');
        addLabel.textContent = 'Sepete ekle';
        status.textContent = error.message || 'Bir hata oluştu.';
        buttons.forEach((button) => { button.disabled = false; });
      }
    }
  }

  if (!customElements.get('wemond-experience-product')) {
    customElements.define('wemond-experience-product', WemondExperienceProduct);
  }
})();
